import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Loader2, Send, Clock, CheckCircle2, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Support() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setTickets((await api.get("/support/tickets")).data.tickets || []); }
    catch { /* non-fatal */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required"); return; }
    setSending(true);
    try {
      const res = await api.post("/support/tickets", { subject: subject.trim(), message: message.trim() });
      setTickets((list) => [res.data.ticket, ...list]);
      setSubject(""); setMessage("");
      toast.success("Support ticket submitted — we'll get back to you by email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit ticket");
    } finally { setSending(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[760px] mx-auto" data-testid="support-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
          <LifeBuoy className="h-7 w-7 text-amber-400" /> Support
        </h1>
        <p className="text-gray-400 mt-1.5 text-sm">
          Have a question or an issue? Send us a ticket and our team will reply to {user?.email || "your account email"}.
        </p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-gray-800 bg-[#0E131F] p-5 space-y-4" data-testid="support-form">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Subject</label>
          <input
            data-testid="support-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="Brief summary of your request"
            className="w-full rounded-lg bg-[#0A0E17] border border-gray-800 px-3.5 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5">Message</label>
          <textarea
            data-testid="support-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={6}
            placeholder="Describe your question or issue in detail…"
            className="w-full rounded-lg bg-[#0A0E17] border border-gray-800 px-3.5 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 resize-y"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            data-testid="support-submit-button"
            disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      </form>

      <div className="mt-10">
        <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2 mb-4">
          <MessageSquare className="h-4 w-4 text-gray-500" /> Your tickets
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 text-amber-400 animate-spin" /></div>
        ) : tickets.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-800 bg-[#0E131F] py-14 text-center" data-testid="support-empty">
            <LifeBuoy className="mx-auto h-9 w-9 text-gray-600" />
            <p className="mt-3 text-gray-400 text-sm">You haven't submitted any tickets yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="support-ticket-list">
            {tickets.map((t, i) => (
              <motion.div
                key={t.id}
                data-testid={`support-ticket-${t.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                className="rounded-xl border border-gray-800 bg-[#0E131F] p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-white">{t.subject}</p>
                  <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                    <CheckCircle2 className="h-3 w-3" /> Submitted
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-300 whitespace-pre-wrap">{t.message}</p>
                <p className="mt-2 text-xs text-gray-600 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(t.created_at)}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
